import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Check, Loader2, Paperclip, X } from "lucide-react";
import axios from "axios";
import { api, API } from "@/lib/api";
import { INQUIRY_TYPES, track, useSettings } from "@/lib/site";
import { OutLink } from "@/components/site/Bits";

const MAX_FILES = 5;
const MAX_MB = 15;
const ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp,.heic,.zip,.doc,.docx,.xls,.xlsx,.kml,.kmz";

function Field({ label, children, optional }) {
    return (
        <label className="block">
            <span className="mono-label text-muted_text mb-2 block">{label}{optional && <span className="normal-case tracking-normal opacity-70"> — optional</span>}</span>
            {children}
        </label>
    );
}

function FileList({ files, onRemove }) {
    if (!files.length) return null;
    return (
        <ul className="mt-3 space-y-2" data-testid="inquiry-file-list">
            {files.map((f, i) => (
                <li key={f.id} className="flex items-center justify-between gap-3 rounded-lg bg-surface px-4 py-2.5 text-sm">
                    <span className="truncate">{f.filename}</span>
                    <button type="button" onClick={() => onRemove(i)} aria-label={`Remove ${f.filename}`} data-testid={`inquiry-file-remove-${i}`} className="text-muted_text hover:text-forest"><X className="h-4 w-4" /></button>
                </li>
            ))}
        </ul>
    );
}

export default function InquiryForm({ defaultType = "General inquiry", lockType = false, projectSlug = "", submitLabel = "Send inquiry", messagePlaceholder }) {
    const s = useSettings();
    const fileRef = useRef(null);
    const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", inquiry_type: defaultType, message: "", consent: false, website: "" });
    const [files, setFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [sending, setSending] = useState(false);
    const [done, setDone] = useState(false);
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

    const onFiles = async (e) => {
        const picked = Array.from(e.target.files || []).slice(0, MAX_FILES - files.length);
        e.target.value = "";
        if (!picked.length) return;
        setUploading(true);
        for (const file of picked) {
            if (file.size > MAX_MB * 1024 * 1024) { toast.error(`${file.name} is larger than ${MAX_MB} MB`); continue; }
            const fd = new FormData();
            fd.append("file", file);
            try {
                const { data } = await axios.post(`${API}/uploads/attachment`, fd);
                setFiles((prev) => [...prev, data]);
            } catch (err) {
                toast.error(err?.response?.data?.detail || `Could not upload ${file.name}`);
            }
        }
        setUploading(false);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.consent) { toast.error("Please accept the privacy notice"); return; }
        setSending(true);
        try {
            await api.post("/inquiries", { ...form, project_slug: projectSlug, attachments: files.map((f) => f.id) });
            track(projectSlug ? "project_info_request" : form.inquiry_type === "I want to invest" ? "investor_inquiry" : "contact_submission", form.inquiry_type);
            setDone(true);
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Please check the form and try again.");
        } finally {
            setSending(false);
        }
    };

    if (done) {
        return (
            <div data-testid="inquiry-success" className="rounded-2xl bg-forest text-cream p-10 md:p-12">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-sage text-forest mb-8"><Check className="h-6 w-6" /></span>
                <p className="font-serif text-3xl md:text-4xl leading-tight mb-4">Thank you, {form.name.split(" ")[0]}.</p>
                <p className="text-cream/80 leading-relaxed max-w-md">We read every message ourselves and will come back to you personally, usually within two working days.</p>
            </div>
        );
    }

    return (
        <form onSubmit={submit} data-testid="inquiry-form" className="space-y-6">
            {!lockType && (
                <fieldset>
                    <legend className="mono-label text-muted_text mb-3">I'm getting in touch because</legend>
                    <div className="flex flex-wrap gap-2" data-testid="inquiry-type-group">
                        {INQUIRY_TYPES.map((t) => (
                            <button type="button" key={t.key} onClick={() => setForm((f) => ({ ...f, inquiry_type: t.value }))}
                                    data-testid={`inquiry-type-${t.key}`} aria-pressed={form.inquiry_type === t.value}
                                    className={`rounded-full border px-4 py-2.5 text-sm transition-colors ${form.inquiry_type === t.value ? "bg-forest border-forest text-cream" : "border-hairline text-ink hover:border-forest"}`}>
                                {t.value}
                            </button>
                        ))}
                    </div>
                </fieldset>
            )}

            {form.inquiry_type === "I have land to sell" && (
                <div data-testid="sell-land-redirect-note" className="rounded-xl bg-sage/30 p-5 text-sm leading-relaxed">
                    Selling land? Our dedicated land submission form lets you share the location, documents and photos in one go.
                    <div className="mt-3 flex flex-wrap gap-4">
                        <Link to="/sell-your-land" data-testid="sell-land-link-en" className="font-medium text-forest underline">Submit your land</Link>
                        <Link to="/pt/vender-terreno" data-testid="sell-land-link-pt" className="font-medium text-forest underline">Versão em português</Link>
                    </div>
                </div>
            )}

            {form.inquiry_type === "I need development financing" && (
                <div data-testid="financing-redirect-note" className="rounded-xl bg-sage/30 p-5 text-sm leading-relaxed">
                    Development financing is provided by <strong>Verde Capital</strong>, a separate business. You can apply directly on their website — or send us a note and we'll make the introduction.
                    <div className="mt-3">
                        <OutLink href={s.verde_capital_url} event="verde_capital_click" label="contact-form" testid="financing-verde-capital-link" className="font-medium text-forest underline">Go to Verde Capital</OutLink>
                    </div>
                </div>
            )}

            <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Name"><input required value={form.name} onChange={set("name")} className="editorial-input" data-testid="inquiry-name" autoComplete="name" /></Field>
                <Field label="Email"><input required type="email" value={form.email} onChange={set("email")} className="editorial-input" data-testid="inquiry-email" autoComplete="email" /></Field>
                <Field label="Company" optional><input value={form.company} onChange={set("company")} className="editorial-input" data-testid="inquiry-company" autoComplete="organization" /></Field>
                <Field label="Phone" optional><input value={form.phone} onChange={set("phone")} className="editorial-input" data-testid="inquiry-phone" autoComplete="tel" /></Field>
            </div>
            <Field label="Message">
                <textarea required rows={5} value={form.message} onChange={set("message")} className="editorial-input resize-y" data-testid="inquiry-message"
                          placeholder={messagePlaceholder || "Tell us about the plot, project or opportunity you have in mind."} />
            </Field>

            <input type="text" name="website" value={form.website} onChange={set("website")} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />

            <div>
                <input ref={fileRef} type="file" multiple accept={ACCEPT} onChange={onFiles} className="hidden" data-testid="inquiry-file-input" />
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading || files.length >= MAX_FILES} data-testid="inquiry-attach-button"
                        className="inline-flex items-center gap-2 rounded-full border border-dashed border-forest/40 px-5 py-2.5 text-sm text-forest hover:border-forest disabled:opacity-50 transition-colors">
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
                    {uploading ? "Uploading…" : "Attach plot or project documents"}
                </button>
                <p className="mt-2 text-xs text-muted_text">Optional. PDF, images, Office, ZIP or KML — up to {MAX_FILES} files, {MAX_MB} MB each.</p>
                <FileList files={files} onRemove={(i) => setFiles((prev) => prev.filter((_, j) => j !== i))} />
            </div>

            <label className="flex items-start gap-3 text-sm text-muted_text leading-relaxed cursor-pointer">
                <input type="checkbox" checked={form.consent} onChange={set("consent")} data-testid="inquiry-consent" className="mt-1 h-4 w-4 accent-forest" />
                <span>I agree that Verde 2050 may store and use my details to respond to this inquiry, as described in the <Link to="/legal/privacy" className="underline hover:text-forest">privacy policy</Link>.</span>
            </label>

            <button type="submit" disabled={sending || uploading} data-testid="inquiry-submit"
                    className="group inline-flex items-center gap-3 rounded-full bg-forest text-cream hover:bg-forest-hover px-8 py-4 text-sm font-medium transition-colors disabled:opacity-60">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {submitLabel}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
        </form>
    );
}
